type PuckBlock = {
  type: string;
  props: Record<string, unknown>;
};

export function normalizeCitationMarkerIndex(marker: unknown): number | undefined {
  if (typeof marker === "number" && Number.isFinite(marker)) {
    return marker;
  }

  if (marker && typeof marker === "object" && "index" in marker) {
    const index = (marker as { index: unknown }).index;
    if (typeof index === "number" && Number.isFinite(index)) {
      return index;
    }
  }

  return undefined;
}

export function normalizeCitationMarkerIndices(
  markers: unknown[] | undefined,
): number[] | undefined {
  if (!markers?.length) return undefined;

  const normalized = markers
    .map(normalizeCitationMarkerIndex)
    .filter((marker): marker is number => marker !== undefined);

  return normalized.length > 0 ? normalized : undefined;
}

function isPuckBlock(value: unknown): value is PuckBlock {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof (value as PuckBlock).type === "string" &&
    "props" in value &&
    typeof (value as PuckBlock).props === "object" &&
    (value as PuckBlock).props !== null
  );
}

function normalizeChatMockupStoredProps(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const messages = props.messages;
  if (!Array.isArray(messages)) return props;

  return {
    ...props,
    messages: messages.map((message) => {
      if (!message || typeof message !== "object") return message;

      const typedMessage = message as {
        citationMarkers?: unknown[];
      };

      return {
        ...typedMessage,
        citationMarkers:
          normalizeCitationMarkerIndices(typedMessage.citationMarkers) ?? [],
      };
    }),
  };
}

function normalizePuckValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) =>
      isPuckBlock(item) ? normalizeChatMockupBlocksInTree(item) : item,
    );
  }

  return value;
}

/** Normalizes nested ChatMockup citation markers to numeric arrays for Puck storage. */
export function normalizeChatMockupBlocksInTree<Block extends PuckBlock>(
  block: Block,
): Block {
  const props: Record<string, unknown> = {
    ...(block.type === "ChatMockup"
      ? normalizeChatMockupStoredProps(block.props)
      : block.props),
  };

  for (const [key, value] of Object.entries(props)) {
    if (key === "id") continue;
    props[key] = normalizePuckValue(value);
  }

  return { ...block, props };
}
